import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UserService } from '../../../../services/user.service';
import { DedupeMatch, LocationsService, PlaceDetail } from '../../../../services/locations.service';
import {
  CONDUCTOR_OUT_DONE,
  ConductorOutAction,
  PlaceRequests,
  RESTORED,
  canManageConductor,
  canOpenProviders,
  conductorOutQuestion,
  toggleDismissed,
} from './place-actions';
import { BLOCK_LABEL, BLOCK_OPTIONS, BlockType, blockTypeFor, kindLabel } from './place-kinds';

const STATUS_LABEL: Record<string, string> = {
  used: 'Provider · used',
  onboarded: 'Provider · not used yet',
  retired: 'Provider · not for new bookings',
  candidate: 'Candidate',
  hidden: 'Hidden in curation',
  unreviewed: 'Not reviewed',
};

/**
 * One crawl place in the Suburbs tool (#253 step 3; plan §12 "Suburb pages", Ivan 2026-10-16): what the crawl knows,
 * whether it is on the website (a provider directly; any other place after a manual review), adding it to Conductor
 * as a provider (the dedupe gate first), our own name for it, and notes. Photos wait for the file library (#258).
 */
@Component({
  selector: 'app-suburb-place',
  templateUrl: './suburb-place.component.html',
  styleUrls: ['./suburb-place.component.scss'],
})
export class SuburbPlaceComponent implements OnChanges {
  @Input() locationId!: number;
  @Input() readOnly = false;
  /** The place changed (listing, name, notes, provider): the host redraws its list and map. */
  @Output() changed = new EventEmitter<PlaceDetail>();
  /** Edit a place added by hand (its details and pin): the host opens the form. */
  @Output() editCustom = new EventEmitter<PlaceDetail>();

  place: PlaceDetail | null = null;
  loading = false;
  message = '';
  more = false;
  /** Our name, while editing it. */
  renaming: string | null = null;
  noteText = '';
  /** Adding to Conductor: the block type, the name, the providers it may already be, and which are dismissed. */
  adding: {
    blockType: BlockType;
    name: string;
    matches: DedupeMatch[] | null;
    dismissed: Set<number>;
  } | null = null;
  /** What "Add to Conductor as …" adds it as. */
  addAs: BlockType = 'venue';

  readonly blockTypes = BLOCK_OPTIONS;

  /** Remove from / restore to Conductor, stop using for new bookings: Content Managers and Operations (Ivan 2026-10-16). */
  readonly canManageConductor: boolean;
  /** Links to a provider's locations: not for content-only accounts (no Service Providers for them). */
  readonly canOpenProviders: boolean;
  private readonly requests: PlaceRequests;

  constructor(
    private api: LocationsService,
    zone: NgZone,
    cdr: ChangeDetectorRef,
    users: UserService
  ) {
    this.canManageConductor = canManageConductor(users);
    this.canOpenProviders = canOpenProviders(users);
    this.requests = new PlaceRequests(zone, () => cdr.detectChanges());
  }

  get busy(): boolean {
    return this.requests.busy;
  }

  get error(): string {
    return this.requests.error;
  }

  ngOnChanges(c: SimpleChanges): void {
    if (c.locationId) {
      this.place = null;
      this.more = false;
      this.renaming = null;
      this.adding = null;
      this.message = '';
      this.load();
    }
  }

  readonly kindLabel = kindLabel;
  readonly blockLabel = BLOCK_LABEL;

  statusLabel(s: string): string {
    return STATUS_LABEL[s] ?? s;
  }

  get socials(): Array<{ key: string; url: string }> {
    return Object.entries(this.place?.socials ?? {}).map(([key, url]) => ({ key, url }));
  }

  get addressLine(): string {
    const a = this.place?.address;
    return a
      ? [a.street, a.suburb, [a.state, a.postcode].filter(Boolean).join(' ')]
          .filter(Boolean)
          .join(', ')
      : '';
  }

  who(x: { firstName: string | null; lastName: string | null }): string {
    return [x.firstName, x.lastName].filter(Boolean).join(' ') || 'someone';
  }

  load(): void {
    if (!this.locationId) return;
    this.loading = true;
    this.requests.run(this.api.place(this.locationId), (p) => {
      this.loading = false;
      this.place = p;
      this.addAs = blockTypeFor(p.kind);
    });
  }

  private updated(p: PlaceDetail, message: string): void {
    this.place = p;
    this.message = message;
    this.changed.emit(p);
  }

  // ------------------------------------------------------------------ the website

  listing(action: 'add' | 'request' | 'approve' | 'reject' | 'remove'): void {
    const p = this.place;
    if (!p) return;
    const said: Record<string, string> = p.provider
      ? {
          add: 'On the website: the "Hide From Website" tag is off.',
          request: 'On the website: the "Hide From Website" tag is off.',
          remove: 'Off the website: the provider is tagged "Hide From Website".',
        }
      : {
          add: 'On the website from the next publish of the suburbs near it.',
          request: 'Sent for review. It goes on the website once someone approves it.',
          approve: 'Approved: it is on the website from the next publish of the suburbs near it.',
          reject: 'Rejected: it stays off the website.',
          remove: 'Off the website from the next publish of the suburbs near it.',
        };
    this.requests.run(this.api.setListing(p.locationId, action), (r) =>
      this.updated(r, said[action] ?? '')
    );
  }

  // ------------------------------------------------------------------ our name

  startRename(): void {
    this.renaming = this.place?.displayName ?? this.place?.crawlName ?? '';
  }

  saveName(): void {
    const p = this.place;
    if (!p || this.renaming === null) return;
    const v = this.renaming.trim();
    this.requests.run(
      this.api.renamePlace(p.locationId, v && v !== p.crawlName ? v : null),
      (r) => {
        this.renaming = null;
        this.updated(
          r,
          r.displayName ? `Renamed to "${r.displayName}".` : "Back to the crawl's name."
        );
      }
    );
  }

  // ------------------------------------------------------------------ notes

  addNote(): void {
    const p = this.place;
    const body = this.noteText.trim();
    if (!p || !body) return;
    this.requests.run(this.api.addPlaceNote(p.locationId, body), (notes) => {
      this.noteText = '';
      this.place = { ...p, notes };
      this.changed.emit(this.place);
    });
  }

  deleteNote(id: number): void {
    const p = this.place;
    if (!p || !window.confirm('Delete this note?')) return;
    this.requests.run(this.api.deletePlaceNote(p.locationId, id), (notes) => {
      this.place = { ...p, notes };
      this.changed.emit(this.place);
    });
  }

  // ------------------------------------------------------------------ out of Conductor

  /** Remove (no arrangement uses it), stop using it for new bookings, or bring it back. Asks first. */
  conductorOut(action: ConductorOutAction): void {
    const p = this.place;
    if (!p?.provider || !window.confirm(conductorOutQuestion(action, p))) return;
    this.requests.run(this.api.setConductor(p.locationId, action), (r) =>
      this.updated(r, CONDUCTOR_OUT_DONE[action])
    );
  }

  /** A provider removed from this place comes back: the same record (soft delete's way back). */
  restore(): void {
    const p = this.place;
    if (!p?.removedProvider) return;
    this.requests.run(this.api.setConductor(p.locationId, 'restore'), (r) =>
      this.updated(r, RESTORED)
    );
  }

  // ------------------------------------------------------------------ add to Conductor (#248's onboarding, with its dedupe gate)

  /** One click: the dedupe gate, then add straight away when nothing looks like it; otherwise the candidates show. */
  startAdd(): void {
    const p = this.place;
    if (!p) return;
    this.adding = { blockType: this.addAs, name: p.name, matches: null, dismissed: new Set() };
    this.requests.run(this.api.onboardCheck(p.locationId), (r) => {
      if (!this.adding) return;
      this.adding.matches = r.matches;
      if (!r.matches.length) this.add();
    });
  }

  toggleDismiss(m: DedupeMatch): void {
    if (this.adding) toggleDismissed(this.adding.dismissed, m.serviceId);
  }

  get canAdd(): boolean {
    const a = this.adding;
    return (
      !!a &&
      !!a.matches &&
      !this.busy &&
      !!a.name.trim() &&
      a.matches.every((m) => !m.hard && a.dismissed.has(m.serviceId))
    );
  }

  add(): void {
    const p = this.place;
    const a = this.adding;
    if (!p || !a || !this.canAdd) return;
    this.requests.run(
      this.api.onboard({
        locationId: p.locationId,
        blockType: a.blockType,
        name: a.name.trim(),
        notDuplicateOf: [...a.dismissed],
      }),
      () => {
        this.adding = null;
        this.requests.run(this.api.place(p.locationId), (r) =>
          this.updated(
            r,
            `Added to Conductor as a ${BLOCK_LABEL[a.blockType].toLowerCase()}. It starts tagged "Hide From Website": use "Add to website" when it should show.`
          )
        );
      }
    );
  }
}
